import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface JournalPostedEvent {
  journalId: number;
  journalNumber: string;
  date: string;
}

export interface FiscalPeriodClosedEvent {
  periodId: number;
  periodName: string;
  closedAt: string;
}

export interface DepreciationPostedEvent {
  assetCount: number;
  totalDepreciation: string;
  periodDate: string;
}

/**
 * Any ledger mutation that invalidates the same views a posted journal does
 * (transaction created/updated/deleted, payment received/refunded, account
 * balance changed). The marker carries the event name only — nothing renders
 * its fields; it exists so those events drive the consumers' ledger refetch
 * instead of broadcasting to nobody.
 */
export interface LedgerMutationEvent {
  type: string;
  at: string;
}

interface AccountingWebSocketState {
  isConnected: boolean;
  lastJournalPosted: JournalPostedEvent | null;
  lastFiscalPeriodClosed: FiscalPeriodClosedEvent | null;
  lastDepreciationPosted: DepreciationPostedEvent | null;
  lastLedgerMutation: LedgerMutationEvent | null;
}

const initialState: AccountingWebSocketState = {
  isConnected: false,
  lastJournalPosted: null,
  lastFiscalPeriodClosed: null,
  lastDepreciationPosted: null,
  lastLedgerMutation: null,
};

export const accountingWebSocketSlice = createSlice({
  name: 'accountingWebSocket',
  initialState,
  reducers: {
    setConnected: (state, action: PayloadAction<boolean>) => {
      state.isConnected = action.payload;
    },
    setLastJournalPosted: (state, action: PayloadAction<JournalPostedEvent>) => {
      state.lastJournalPosted = action.payload;
    },
    setLastFiscalPeriodClosed: (state, action: PayloadAction<FiscalPeriodClosedEvent>) => {
      state.lastFiscalPeriodClosed = action.payload;
    },
    setLastDepreciationPosted: (state, action: PayloadAction<DepreciationPostedEvent>) => {
      state.lastDepreciationPosted = action.payload;
    },
    setLastLedgerMutation: (state, action: PayloadAction<LedgerMutationEvent>) => {
      state.lastLedgerMutation = action.payload;
    },
  },
});

export const {
  setConnected,
  setLastJournalPosted,
  setLastFiscalPeriodClosed,
  setLastDepreciationPosted,
  setLastLedgerMutation,
} = accountingWebSocketSlice.actions;

export default accountingWebSocketSlice.reducer;
